import cx from 'classnames'
import {ReactComponent as DeleteIcon} from 'icons/delete.svg'
import {ReactComponent as EditIcon} from 'icons/edit.svg'
import {ReactComponent as LeftIcon} from 'icons/left.svg'
import {ButtonHTMLAttributes, MouseEvent, ReactNode} from 'react'

type Props = {
  children: ReactNode
  disabled?: boolean
  className?: string
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
  expanded?: boolean
  autoExpanded?: boolean
  type?: ButtonHTMLAttributes<unknown>['type']
  form?: string
}

export const Button = ({children, disabled, className, onClick, expanded, type, autoExpanded, form}: Props) => {
  return (
    <button
      disabled={disabled}
      className={cx(
        'hover:opacity-80 select-none',
        expanded ? 'w-full' : 'w-fit',
        disabled ? 'cursor-auto pointer-events-none' : 'cursor-pointer pointer-events-auto',
        {'w-full d:w-fit': autoExpanded},
        className
      )}
      onClick={onClick}
      type={type}
      form={form}
    >
      {children}
    </button>
  )
}

Button.Standard = ({children, className, ...other}: Props) => {
  return (
    <Button className={cx('flex items-center justify-center rounded-lg font-base h-[50px] px-8', className)} {...other}>
      {children}
    </Button>
  )
}

Button.Primary = {
  Orange: ({className, disabled, ...other}: Props) => {
    return (
      <Button.Standard
        {...other}
        disabled={disabled}
        className={cx('text-white font-bold', disabled ? 'bg-inactive-gray' : 'bg-theme', className)}
      />
    )
  },
  Red: ({className, disabled, ...other}: Props) => {
    return (
      <Button.Standard
        {...other}
        disabled={disabled}
        className={cx('text-white font-bold', disabled ? 'bg-inactive-gray' : 'bg-[#EA4335]', className)}
      />
    )
  },
}

Button.Secondary = {
  Black: ({className, disabled, ...other}: Props) => {
    return (
      <Button.Standard
        {...other}
        disabled={disabled}
        className={cx(
          'border-solid border font-bold',
          disabled ? 'text-gray-4 border-gray-4' : 'border-french-blue-base',
          className
        )}
      />
    )
  },

  Orange: ({className, disabled, ...other}: Props) => {
    return (
      <Button.Standard
        {...other}
        disabled={disabled}
        className={cx(
          'border-solid border font-bold',
          disabled ? 'text-gray-4 border-gray-4' : 'border-theme',
          className
        )}
      />
    )
  },
}

Button.Simple = ({className, disabled, ...other}: Props) => {
  return (
    <Button
      {...other}
      disabled={disabled}
      className={cx(disabled ? 'text-gray-2' : 'text-french-blue-040', className)}
    />
  )
}

type GrayButtonProps = {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
}

Button.Gray = ({onClick, children, disabled}: GrayButtonProps) => {
  return (
    <Button
      disabled={disabled}
      className={cx(
        'text-base !font-bold !px-3 !py-2 flex gap-1 items-center !rounded text-text-secondary !bg-[#F0F0F0]',
        disabled ? 'opacity-50' : ''
      )}
      onClick={onClick}
    >
      {children}
    </Button>
  )
}

Button.Edit = ({onClick, disabled}: Pick<GrayButtonProps, 'onClick' | 'disabled'>) => {
  return (
    <Button.Gray onClick={onClick} disabled={disabled}>
      <EditIcon />
      <span>Edit</span>
    </Button.Gray>
  )
}

Button.Delete = ({onClick, disabled}: Pick<GrayButtonProps, 'onClick' | 'disabled'>) => {
  return (
    <Button.Gray onClick={onClick} disabled={disabled}>
      <DeleteIcon />
      <span>Delete</span>
    </Button.Gray>
  )
}

Button.EditChoice = ({onClick, disabled}: Pick<GrayButtonProps, 'onClick' | 'disabled'>) => {
  return (
    <Button.Gray onClick={onClick} disabled={disabled}>
      <LeftIcon />
      <span>Edit choice</span>
    </Button.Gray>
  )
}

Button.Back = ({className, disabled, ...other}: Omit<Props, 'children'>) => {
  return (
    <Button.Secondary.Black {...other} disabled={disabled} className={cx('w-12 min-w-12 !px-0', className)}>
      <LeftIcon className="mx-auto" />
    </Button.Secondary.Black>
  )
}
